import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { IconPhone, IconSparkles } from './icons';

/**
 * Centered dialog that surfaces the "Enable call audio" gesture the focused
 * view needs before Twilio Device init. Browsers gate AudioContext behind a
 * user gesture, so the prompt has to be a deliberate click — this dialog gives
 * that click prominent placement on entry to the view.
 *
 * Controlled by `FocusedLeadView`: `open` is `callState.status === 'uninit'`
 * minus a dismissal flag so users who close it stay closed until the next
 * uninit transition (e.g. token expiration / device offline).
 */
export type EnableAudioDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onEnable: () => void;
};

export const EnableAudioDialog: FC<EnableAudioDialogProps> = ({ open, onOpenChange, onEnable }) => {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<div className={css(styles.iconRow)}>
						<span className={css(styles.iconBadge)}>
							<IconSparkles size={18} sw={2} />
						</span>
					</div>
					<DialogTitle>Turn on call audio</DialogTitle>
					<DialogDescription>
						Your browser needs a click before it will let us connect your microphone and Twilio. Enable audio now and
						you'll be able to take and place calls from this view.
					</DialogDescription>
				</DialogHeader>
				<DialogBody />
				<DialogFooter>
					<Button variant='primary' size='lg' onClick={onEnable}>
						<IconPhone size={14} />
						Enable call audio
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	popup: {
		maxWidth: 440,
	},
	iconRow: {
		display: 'flex',
		justifyContent: 'center',
		marginBottom: 8,
	},
	iconBadge: {
		width: 44,
		height: 44,
		borderRadius: 12,
		background: 'var(--accent-soft)',
		color: 'var(--accent-ink)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});
